import React, { useEffect, useState } from 'react';

import { CompetenciesSummary } from './summaries/CompetenciesSummary';
import { Login } from './Login';
import { useGlobalContext, useQuery } from '../hooks';
import { useSearchParams } from 'react-router-dom'
import { 
  FCMENU_EMPLOYEE_ID_COOKIE,
  FCMENU_EMPLOYEE_LOGIN_COOKIE,
  FCMENU_LOCALE_COOKIE,
  FCMENU_LOCALE_TOKEN_KEY,
  FCMENU_LOGIN_TOKEN_KEY,
  FCMENU_MOC_DUMMY_USER_TOKEN,
  FCMENU_SENSITIVE_COOKIE, 
  FCMENU_WAREHOUSE_ID_COOKIE
} from '../constants/loginConfiguration';
import { useErrorHandler } from 'react-error-boundary';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { Flex } from '@amzn/stencil-react-components/layout';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES } from '../constants/languages';
import { CA_WAREHOUSE_FRENCH_DEFAULTS } from 'src/constants/featureGates';

export const Home = ({}) => {
  const query = useQuery();
  const { isLoggedIn, setUserData, backendServiceClient, userDetails } = useGlobalContext();
  const [_searchParams, setSearchParams] = useSearchParams();
  const handleError = useErrorHandler();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const getFcMenuCookie = (name: string) => {
    // Cookie list with whitespaces removed
    const cookieList = document.cookie
      .split(';')
      .map((cookie) => cookie.trim());

    // Return cookie value
    return cookieList
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  };

  useEffect(() => {
    if (query.get(FCMENU_SENSITIVE_COOKIE) || getFcMenuCookie(FCMENU_EMPLOYEE_LOGIN_COOKIE)) {
      // This cookie will only be present when using FcMenu-External (MOC)
      const fcMenuEmployeeLoginFromCookie = getFcMenuCookie(FCMENU_EMPLOYEE_LOGIN_COOKIE);
  
      // User Token is required to make a APIG call from the CORP PAK 2.0 endpoint.
      const userToken = fcMenuEmployeeLoginFromCookie !== undefined ? FCMENU_MOC_DUMMY_USER_TOKEN : query.get(FCMENU_SENSITIVE_COOKIE) || "";

      (async () => {
        try {
          setLoading(true);

          /**
           * If the request is coming from CORP, a token will be provided in the URL.
           * If the request is coming from MOC, we need to get the data from the cookies.
           */
          let userTokenFields = fcMenuEmployeeLoginFromCookie ? userToken : JSON.parse(atob(userToken!.split('.')[1]));
          let userLogin = fcMenuEmployeeLoginFromCookie || userTokenFields[FCMENU_LOGIN_TOKEN_KEY];

          const userData = await backendServiceClient.getEmployeeDetails(userToken, userLogin);

          setUserData(userToken, {
            isAdmin: userData.isAdmin!,
            managerLogin: userData.managerLogin!,
            emailAddress: userData.emailAddress!,
            locale: userData.locale!,
            employeeId: getFcMenuCookie(FCMENU_EMPLOYEE_ID_COOKIE),
            login: getFcMenuCookie(FCMENU_EMPLOYEE_LOGIN_COOKIE),
            warehouseId: getFcMenuCookie(FCMENU_WAREHOUSE_ID_COOKIE),
          });

          /**
           * Note: FC Menu returns locales like en_US, es_ES, etc
           * but i18next expects these to be in the format en-US, es-ES, etc
           *
           * Canada sites to use FRENCH (fr-CA) translations by default.
           * Note: The Phase1 Allowlist is only CA sites.
           */
          const isCanadaSite = CA_WAREHOUSE_FRENCH_DEFAULTS.includes(userData.warehouseId!);

          let userLocale = isCanadaSite ? 'fr-CA' : getFcMenuCookie(FCMENU_LOCALE_COOKIE) || userTokenFields[FCMENU_LOCALE_TOKEN_KEY];
          userLocale.replace("_", "-");

          if (!LOCALES.includes(userLocale)) {
            userLocale = DEFAULT_LOCALE;
          }
          i18n.changeLanguage(userLocale);

          setLoading(false);
        } catch (error) {
          handleError(new Error(t('loginFailure')));
        }
      })();

      // Remove query params from the URL
      setSearchParams([]);
    }
  }, []);

  let content;
  if (loading || query.get(FCMENU_SENSITIVE_COOKIE)) {
    content = <Flex flexDirection="row" justifyContent="center" alignItems="center">
      <Spinner showText loadingText={t('loggingIn')} />
    </Flex>
  } else if (isLoggedIn) {
    content = <CompetenciesSummary />;
  } else {
    content = <Login />;
  }

  return (
    <>
      {content}
    </>
  );
}
